/**
 * Phase 86 Plan 86-03 Task 2 — a full-width Compost zone header band
 * (FRESH / WARM / STALE / GRAVEYARD). Rendered between groups of rows on
 * the default "Rot" sort mode; 86-04 mounts these around `MyTaskRow` groups.
 *
 * Accessibility (README "Accessibility" section): "Zone bands should be
 * real headings ... or `aria-labelledby` on a `role="group"` per zone so
 * screen readers can jump between zones." This renders the group role with
 * an accessible name assembled from the zone label and the count.
 */
import type { ReactNode } from 'react';
import { ZONE_BAND_STYLE, type CompostBandZone } from './compostTokens';

const ZONE_SUBCOPY: Record<CompostBandZone, string> = {
  fresh: 'touched today or yesterday — keep it that way',
  warm: '2–5 days quiet · one comment away from fine',
  stale: 'the emoji your colleagues can see too',
  graveyard: 'either bury them properly, or bring one back',
};

/** FRESH/WARM bands are 7px vertical padding; STALE/GRAVEYARD are 8px. */
const ZONE_PADDING_Y: Record<CompostBandZone, string> = {
  fresh: 'py-[7px]',
  warm: 'py-[7px]',
  stale: 'py-2',
  graveyard: 'py-2',
};

export interface MtZoneBandProps {
  zone: CompostBandZone;
  count: number;
  /** Optional trailing slot — 86-05 drops the BULK CLOSE chip in here for graveyard. */
  trailing?: ReactNode;
  className?: string;
}

export function MtZoneBand({ zone, count, trailing, className = '' }: MtZoneBandProps) {
  const style = ZONE_BAND_STYLE[zone];
  const accessibleName = `${style.label} zone, ${count} task${count === 1 ? '' : 's'}`;

  return (
    <div
      role="group"
      aria-label={accessibleName}
      data-testid={`mt-zone-band-${zone}`}
      className={`flex items-center gap-2.5 px-[18px] ${ZONE_PADDING_Y[zone]} border-b border-ms-border-subtle ${className}`}
      style={{ background: style.background }}
    >
      {style.glyph && (
        <span style={{ fontSize: 14, lineHeight: 1 }} aria-hidden="true">
          {style.glyph}
        </span>
      )}
      <span
        className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] shrink-0"
        style={{ color: style.labelColorHex }}
      >
        {style.label} · {count}
      </span>
      <span className="text-[10.5px]" style={{ color: style.subCopyHex }}>
        {ZONE_SUBCOPY[zone]}
      </span>
      {trailing && <span className="ml-auto shrink-0">{trailing}</span>}
    </div>
  );
}
